import React, { useEffect, useState } from "react";
import {
  Pill,
  Syringe,
  Calendar,
  User,
  Hash,
  MapPin,
  Search,
  ArrowUpDown,
  FileCheck,
  ChevronLeft,
  ChevronRight,
  XCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  MedicationHistoryEntry,
  Prescription,
} from "@/lib/dashboard/prescriptionsData";
import { Box, Skeleton } from "@mui/material";
import { usePagination } from "@/components/ui/table-pagination";
import { getVisiblePages } from "@/lib/utils";
import useTitanApp from "@/hooks/useTitanApp";
import {
  addPatientDocuments,
  getPatientDocuments,
} from "@/dispatchers/patientDispatchers";

interface MedicationHistoryProps {
  history: MedicationHistoryEntry[];
  isDarkMode: boolean;
  onGenerateVaccineCertificate?: (
    vaccines: MedicationHistoryEntry[],
  ) => Promise<string | null>;
  patientPrescriptionsLoader: boolean;
}

export function MedicationHistory({
  history,
  isDarkMode,
  onGenerateVaccineCertificate,
  patientPrescriptionsLoader,
}: MedicationHistoryProps) {
  const [filter, setFilter] = useState<"all" | "medication" | "vaccination">(
    "all",
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc"); // desc = newest first
  const [selectedVaccines, setSelectedVaccines] = useState<Set<string>>(
    new Set(),
  );

  const pagination = usePagination(5);
  const { currentPage, itemsPerPage, handlePageChange, getPaginatedData } =
    pagination;

  const getUniqueEntryKey = (entry: MedicationHistoryEntry): string => {
    return `${entry.id}_${entry.date}_${entry.medicationName}_${entry.batchNumber || ""}_${entry.dose || ""}`;
  };

  useEffect(() => {
    setSelectedVaccines(new Set());
    setFilter("all");
    setSearchQuery("");
    setSortOrder("desc");
    handlePageChange(1);
  }, [history]);

  // Clear selections when filter changes
  const handleFilterChange = (
    newFilter: "all" | "medication" | "vaccination",
  ) => {
    setFilter(newFilter);
    setSelectedVaccines(new Set());
    handlePageChange(1);
  };

  const { patientState } = useTitanApp();
  const { selectedPatientId, selectedPatientConsultation, consultationId } =
    patientState || {};

  const filteredHistory = history
    .filter((entry) => {
      if (filter !== "all" && entry.type !== filter) return false;

      if (searchQuery.trim() !== "") {
        return entry.medicationName
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
      }

      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
    });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const { paginatedData: paginatedHistory, totalPages } =
    getPaginatedData(filteredHistory);

  const getEntryIndexKey = (index: number) => `${currentPage}-${index}`;

  // Toggle vaccine selection using page-index key
  const toggleVaccineSelection = (itemKey: string) => {
    setSelectedVaccines((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemKey)) {
        newSet.delete(itemKey);
      } else {
        newSet.add(itemKey);
      }
      return newSet;
    });
  };

  // Select all vaccinations visible on the current page
  const selectAllVaccines = () => {
    const allVaccineKeys = new Set(
      paginatedHistory
        .filter((entry) => entry.type === "vaccination")
        .map((_, index) => getEntryIndexKey(index)),
    );
    setSelectedVaccines(allVaccineKeys);
  };

  // Clear all selections
  const clearAllSelections = () => {
    setSelectedVaccines(new Set());
  };

  const handleGenerateCertificate = async () => {
    const selectedVaccineEntries = paginatedHistory.filter((entry, index) =>
      selectedVaccines.has(getEntryIndexKey(index)),
    );

    const base64Image = await onGenerateVaccineCertificate?.(
      selectedVaccineEntries,
    );

    if (base64Image) {
      const cleanedBase64Image = base64Image.replace(
        /^data:image\/png;base64,/,
        "",
      );
      const sizeInBytes = Math.round((cleanedBase64Image.length * 3) / 4);
      const sizeInKB = Math.round(sizeInBytes / 1024);
      const payload = {
        patientId: selectedPatientId || selectedPatientConsultation?.patientId,
        title: "Vaccination Certificate",
        description: "Generated Vaccination Certificate",
        size: `${sizeInKB} KB`,
        status: 1,
        fileContent: cleanedBase64Image,
        contentType: "image/jpeg",
        type: 7,
        consultationId: consultationId,
      };

      try {
        await addPatientDocuments(payload);
        await getPatientDocuments(
          selectedPatientId || selectedPatientConsultation?.patientId,
        );
      } catch (error) {
        console.error("Failed to store generated certificate:", error);
      }
    }

    setSelectedVaccines(new Set());
  };

  const handlePageChangeSafe = (page: number) => {
    setSelectedVaccines(new Set());
    handlePageChange(page);
  };
  if (patientPrescriptionsLoader) {
    return (
      <div
        className={`rounded-xl p-4 transition-colors duration-300 ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        } shadow-sm`}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Pill
              className={`w-4 h-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            />
            <h3
              className={`text-sm font-semibold transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Medication History
            </h3>
          </div>
        </div>

        {/* Skeleton loader cards */}
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Box
              key={i}
              className={`border rounded-lg p-3 ${
                isDarkMode ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <div className="flex items-start gap-3">
                <Skeleton
                  variant="rectangular"
                  width={48}
                  height={48}
                  className="rounded-lg"
                />
                <div className="flex-1 space-y-2">
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={16}
                    className="rounded"
                  />
                  <Skeleton
                    variant="text"
                    width="40%"
                    height={14}
                    className="rounded"
                  />
                </div>
              </div>
            </Box>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl p-4 transition-colors duration-300 ${
        isDarkMode ? "bg-gray-800" : "bg-white"
      } shadow-sm`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Pill
            className={`w-4 h-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          />
          <h3
            className={`text-sm font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Medication History
          </h3>
        </div>
        <Badge variant="secondary" className="text-xs">
          {filteredHistory.length}
        </Badge>
      </div>

      {/* Filter Buttons */}
      <div className="flex gap-2 mb-4">
        <Button
          variant={filter === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => handleFilterChange("all")}
          className={`text-xs h-7 ${
            filter === "all"
              ? isDarkMode
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-blue-600 hover:bg-blue-700"
              : isDarkMode
                ? "bg-gray-700 hover:bg-gray-600 border-gray-600"
                : ""
          }`}
        >
          All
        </Button>
        <Button
          variant={filter === "medication" ? "default" : "outline"}
          size="sm"
          onClick={() => handleFilterChange("medication")}
          className={`text-xs h-7 ${
            filter === "medication"
              ? isDarkMode
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-blue-600 hover:bg-blue-700"
              : isDarkMode
                ? "bg-gray-700 hover:bg-gray-600 border-gray-600"
                : ""
          }`}
        >
          <Pill className="w-3 h-3 mr-1" />
          Medications
        </Button>
        <Button
          variant={filter === "vaccination" ? "default" : "outline"}
          size="sm"
          onClick={() => handleFilterChange("vaccination")}
          className={`text-xs h-7 ${
            filter === "vaccination"
              ? isDarkMode
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-blue-600 hover:bg-blue-700"
              : isDarkMode
                ? "bg-gray-700 hover:bg-gray-600 border-gray-600"
                : ""
          }`}
        >
          <Syringe className="w-3 h-3 mr-1" />
          Vaccinations
        </Button>
      </div>

      {/* Vaccination Selection Controls */}
      {filter === "vaccination" && filteredHistory.length > 0 && (
        <div
          className={`mb-4 p-3 rounded-lg border ${
            isDarkMode
              ? "bg-emerald-900/20 border-emerald-700/50"
              : "bg-emerald-50 border-emerald-200"
          }`}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <FileCheck
                className={`w-4 h-4 ${
                  isDarkMode ? "text-emerald-400" : "text-emerald-600"
                }`}
              />
              <span
                className={`text-xs font-medium ${
                  isDarkMode ? "text-emerald-300" : "text-emerald-700"
                }`}
              >
                {selectedVaccines.size} selected
              </span>
            </div>
            <div className="flex items-center gap-2">
              {selectedVaccines.size > 0 ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllSelections}
                    className={`text-xs h-6 px-2 ${
                      isDarkMode
                        ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleGenerateCertificate}
                    className={`text-xs h-6 px-3 ${
                      isDarkMode
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                        : "bg-emerald-600 hover:bg-emerald-700 text-white"
                    }`}
                  >
                    <FileCheck className="w-3 h-3 mr-1" />
                    Generate Certificate
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={selectAllVaccines}
                  className={`text-xs h-6 px-2 ${
                    isDarkMode
                      ? "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/20"
                      : "text-emerald-600 hover:text-emerald-700"
                  }`}
                >
                  Select All
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search and Sort Controls */}
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search
            className={`absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          />
          <Input
            type="text"
            placeholder="Search by drug name..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              handlePageChange(1);
            }}
            className={`pl-8 h-7 text-xs transition-colors duration-300 ${
              isDarkMode
                ? "bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                : "bg-white border-gray-300"
            }`}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
          className={`text-xs h-7 px-3 transition-colors duration-300 ${
            isDarkMode
              ? "bg-gray-700 hover:bg-gray-600 border-gray-600 text-gray-200"
              : ""
          }`}
          title={sortOrder === "desc" ? "Newest first" : "Oldest first"}
        >
          <ArrowUpDown className="w-3.5 h-3.5 mr-1" />
          {sortOrder === "desc" ? "Newest" : "Oldest"}
        </Button>
      </div>

      <div className="space-y-3 overflow-y-auto max-h-96">
        {filteredHistory.length === 0 ? (
          <div
            className={`text-center py-8 border rounded-lg transition-colors duration-300 ${
              isDarkMode
                ? "bg-gray-700 border-gray-600 text-gray-400"
                : "bg-gray-50 border-gray-200 text-gray-500"
            }`}
          >
            {filter === "vaccination" ? (
              <Syringe className="w-6 h-6 mx-auto mb-2 opacity-50" />
            ) : (
              <Pill className="w-6 h-6 mx-auto mb-2 opacity-50" />
            )}
            <p className="text-xs">
              No {filter !== "all" ? filter : "medication"} history found
            </p>
          </div>
        ) : (
          paginatedHistory?.map((entry, index) => {
            const itemKey = getEntryIndexKey(index);
            return (
              <div
                key={itemKey}
                className={`border rounded-lg p-3 transition-colors duration-300 ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-700/50"
                    : "border-gray-200 bg-white"
                } ${
                  filter === "vaccination" && selectedVaccines.has(itemKey)
                    ? isDarkMode
                      ? "border-emerald-600 bg-emerald-900/20"
                      : "border-emerald-400 bg-emerald-50"
                    : ""
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start flex-1 gap-2">
                    {/* Checkbox for vaccination multi-select */}
                    {filter === "vaccination" &&
                      entry.type === "vaccination" && (
                        <Checkbox
                          checked={selectedVaccines.has(itemKey)}
                          onCheckedChange={() =>
                            toggleVaccineSelection(itemKey)
                          }
                          className={`mt-1 ${
                            isDarkMode
                              ? "border-emerald-600 data-[state=checked]:bg-emerald-600"
                              : "border-emerald-600 data-[state=checked]:bg-emerald-600"
                          }`}
                        />
                      )}
                    {entry.type === "vaccination" ? (
                      <Syringe
                        className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }`}
                      />
                    ) : (
                      <Pill
                        className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                          isDarkMode ? "text-purple-400" : "text-purple-600"
                        }`}
                      />
                    )}
                    <div className="flex-1">
                      <div
                        className={`text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {entry.medicationName}
                      </div>
                      <div
                        className={`text-xs mt-0.5 transition-colors duration-300 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {entry.dose}
                      </div>
                    </div>
                  </div>
                  {entry.type !== "vaccination" && (
                    <Badge
                      variant="secondary"
                      className={`text-xs ${
                        entry.status === "Approved"
                          ? "text-green-600"
                          : entry.status === "Pending"
                            ? "text-blue-600"
                            : entry.status === "Rejected"
                              ? "bg-red-100 text-red-700 border-red-300"
                              : "text-red-600"
                      }`}
                    >
                      {entry.status === "Rejected" ? (
                        <div className="flex items-center gap-1">
                          <XCircle className="w-3 h-3" />
                          <span>Declined</span>
                        </div>
                      ) : (
                        entry.status
                      )}
                    </Badge>
                  )}
                </div>

                {/* Date */}
                <div
                  className={`flex items-center gap-1.5 text-xs mt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  <Calendar className="w-3 h-3" />
                  <span>{formatDate(entry.date)}</span>
                </div>

                {/* Vaccination-specific details */}
                {entry.type === "vaccination" && (
                  <div className="mt-2 space-y-1">
                    {entry.administeredBy && (
                      <div
                        className={`flex items-center gap-1.5 text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        <User className="w-3 h-3" />
                        <span>Administered by: {entry.administeredBy}</span>
                      </div>
                    )}
                    {entry.site && (
                      <div
                        className={`flex items-center gap-1.5 text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        <MapPin className="w-3 h-3" />
                        <span>Site: {entry.site}</span>
                      </div>
                    )}
                    {entry.batchNumber && (
                      <div
                        className={`flex items-center gap-1.5 text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        <Hash className="w-3 h-3" />
                        <span>Batch: {entry.batchNumber}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes */}
                {entry.notes && (
                  <div
                    className={`mt-2 text-xs p-2 rounded transition-colors duration-300 ${
                      isDarkMode
                        ? "bg-gray-800 text-gray-300"
                        : "bg-gray-50 text-gray-600"
                    }`}
                  >
                    {entry.notes}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination Controls */}
      {!patientPrescriptionsLoader &&
        filteredHistory.length > itemsPerPage &&
        totalPages > 1 && (
          <div
            className={`flex items-center justify-between mt-4 pt-3 border-t ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div
              className={`text-xs ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, filteredHistory.length)} of{" "}
              {filteredHistory.length}
            </div>

            <div className="flex flex-wrap items-center max-w-full gap-1">
              <button
                onClick={() => handlePageChangeSafe(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-1 rounded transition-colors duration-300 ${
                  currentPage === 1
                    ? isDarkMode
                      ? "text-gray-600 cursor-not-allowed"
                      : "text-gray-400 cursor-not-allowed"
                    : isDarkMode
                      ? "text-gray-300 hover:bg-gray-700"
                      : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {(() => {
                const { start, end } = getVisiblePages(
                  currentPage,
                  totalPages,
                  5,
                );

                return (
                  <>
                    {start > 1 && (
                      <>
                        <button
                          onClick={() => handlePageChangeSafe(1)}
                          className="px-2 py-1 text-xs text-gray-600 rounded hover:bg-gray-100"
                        >
                          1
                        </button>
                        <span className="px-1 text-xs text-gray-400">…</span>
                      </>
                    )}

                    {Array.from(
                      { length: end - start + 1 },
                      (_, i) => start + i,
                    ).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChangeSafe(page)}
                        className={`px-2 py-1 text-xs rounded transition-colors ${
                          page === currentPage
                            ? isDarkMode
                              ? "bg-blue-600 text-white"
                              : "bg-blue-500 text-white"
                            : isDarkMode
                              ? "text-gray-300 hover:bg-gray-700"
                              : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {page}
                      </button>
                    ))}

                    {end < totalPages && (
                      <>
                        <span className="px-1 text-xs text-gray-400">…</span>
                        <button
                          onClick={() => handlePageChangeSafe(totalPages)}
                          className="px-2 py-1 text-xs text-gray-600 rounded hover:bg-gray-100"
                        >
                          {totalPages}
                        </button>
                      </>
                    )}
                  </>
                );
              })()}

              <button
                onClick={() => handlePageChangeSafe(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-1 rounded transition-colors duration-300 ${
                  currentPage === totalPages
                    ? isDarkMode
                      ? "text-gray-600 cursor-not-allowed"
                      : "text-gray-400 cursor-not-allowed"
                    : isDarkMode
                      ? "text-gray-300 hover:bg-gray-700"
                      : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
    </div>
  );
}
